import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import RENav from "./RENav";
import { sendContact } from "@/lib/api";
import LegalLinks from "@/components/LegalLinks";

export default function PropertyPage() {
  const { t } = useLang();
  const p = t.property;
  const [form, setForm] = useState({ name: "", email: "", neighbourhood: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await sendContact("property", form.name, form.email, { Neighbourhood: form.neighbourhood });
      setStatus("sent");
      setForm({ name: "", email: "", neighbourhood: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inputCls = "bg-transparent border border-paper/35 text-paper placeholder:text-paper/45 focus:border-brass px-3.5 py-3 font-body text-sm transition-colors";

  return (
    <main className="grain bg-stone text-charcoal" data-testid="property-page">
      <RENav
        theme="light"
        word={t.nav.property}
        links={[
          { href: "#what", label: p.navWhat },
          { href: "#how", label: p.navHow },
          { href: "#waitlist", label: p.navJoin },
        ]}
      />

      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-7">
              <Reveal>
                <span className="font-mono-data text-[0.62rem] text-crimson block mb-5">{p.kicker}</span>
                <h1 className="font-heading font-semibold text-[2.5rem] md:text-[4.2rem] leading-[1.02] max-w-[15ch]">{p.h1}</h1>
                <p className="text-[1.1rem] leading-[1.6] text-charcoal/80 max-w-[46ch] mt-6 mb-8">{p.intro}</p>
                <a href="#waitlist" data-testid="property-hero-cta" className="inline-flex items-center gap-2 font-mono-data text-[0.66rem] bg-charcoal text-stone px-5 py-3.5 hover:bg-crimson transition-colors">
                  {p.cta} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div className="relative w-full h-[34vh] md:h-[40vh] overflow-hidden">
                  <img src="/assets/re-apartment.webp" alt="Rental apartment in Tirana" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="what" className="py-16 md:py-20">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal className="flex items-baseline justify-between gap-6 flex-wrap mb-10">
            <h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.4rem]">{p.whatTitle}</h2>
            <span className="font-mono-data text-[0.62rem] text-charcoal/55">{p.whatMeta}</span>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {p.what.map((card, i) => (
              <Reveal key={card.label} delay={i * 0.08}>
                <article className="border-t-2 border-brass pt-4" data-testid={`property-what-${i}`}>
                  <span className="font-mono-data text-[0.62rem] text-brass block mb-3">{card.label}</span>
                  <h3 className="font-heading font-semibold text-[1.25rem] mb-2.5">{card.title}</h3>
                  <p className="text-[0.95rem] leading-[1.6] text-charcoal/75">{card.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="py-16 md:py-20">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal className="flex items-baseline justify-between gap-6 flex-wrap mb-10">
            <h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.4rem]">{p.howTitle}</h2>
            <span className="font-mono-data text-[0.62rem] text-charcoal/55">{p.howMeta}</span>
          </Reveal>
          <div className="grid gap-0">
            {p.steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.06}>
                <div className="grid grid-cols-[64px_1fr] md:grid-cols-[90px_1fr] gap-5 md:gap-8 py-6 border-b border-charcoal/15 first:pt-0" data-testid={`property-step-${i}`}>
                  <span className="font-heading italic text-brass text-[2rem] md:text-[2.2rem] leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h4 className="font-heading font-semibold text-[1.15rem] mb-1.5">{step.title}</h4>
                    <p className="text-[0.94rem] leading-[1.6] text-charcoal/80 max-w-[56ch]">{step.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="waitlist" className="py-16 md:py-20">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-0 bg-ink text-paper overflow-hidden">
              <div className="relative p-8 md:p-12 flex flex-col justify-center">
                <img src="/assets/medallion.png" alt="" className="absolute top-8 right-8 w-20 opacity-10 rounded-full" />
                <h2 className="font-heading font-semibold italic text-[1.7rem] md:text-[2.2rem] mb-3">{p.waitTitle}</h2>
                <p className="text-paper/80 max-w-[42ch]">{p.waitText}</p>
              </div>
              <div className="p-8 md:p-12 bg-ink-soft">
                {status === "sent" ? (
                  <p data-testid="property-form-success" className="font-body text-sm text-brass border border-brass/40 px-4 py-6">{t.form.sent}</p>
                ) : (
                  <form className="grid gap-3" onSubmit={submit} data-testid="property-waitlist-form">
                    <input data-testid="property-form-name" type="text" required placeholder={t.form.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                    <input data-testid="property-form-email" type="email" required placeholder={t.form.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                    <input data-testid="property-form-neighbourhood" type="text" placeholder={p.neighbourhood} value={form.neighbourhood} onChange={(e) => setForm({ ...form, neighbourhood: e.target.value })} className={inputCls} />
                    <button data-testid="property-form-submit" type="submit" disabled={status === "sending"} className="justify-self-start mt-1 font-mono-data text-[0.66rem] px-5 py-3 transition-colors disabled:opacity-50 bg-brass text-ink hover:bg-paper">
                      {status === "sending" ? t.form.sending : p.waitButton}
                    </button>
                    {status === "error" && <p data-testid="property-form-error" className="text-[0.75rem] text-crimson">{t.form.error}</p>}
                    <p className="text-[0.7rem] mt-1 text-paper/55">{p.waitNote}</p>
                  </form>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="py-10">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8 flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-6">
          <div>
            <p className="font-heading font-semibold uppercase tracking-[0.16em] text-charcoal text-[1.05rem]">{p.footerBrand}</p>
            <p className="font-mono-data text-[0.6rem] tracking-[0.2em] text-charcoal/50 mt-1.5">{t.common.company}</p>
          </div>
          <LegalLinks className="font-mono-data text-[0.6rem] tracking-[0.2em] text-charcoal/50 order-last w-full text-center lg:order-none lg:w-auto" />
          <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.2em] text-charcoal/50">© {new Date().getFullYear()} {p.footerBrand}</span>
        </div>
      </footer>
    </main>
  );
}
